import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Avatar,
  IconButton,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { PhotoCamera, Business } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Facility, FacilityFormData } from '../../types/facility';
import { facilityApi } from '../../features/facilities/api/facilityApi';

import { hospitalApi, Hospital } from '../../api/hospitalApi';
import { useFacilities } from '../../features/facilities';

// Real hospital list for company profile dropdown

const FacilityForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Use facilities to derive unique hospitals for the dropdown
  const { facilities } = useFacilities();
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [hospitalLoading, setHospitalLoading] = useState(false);

  const [formData, setFormData] = useState<FacilityFormData>({
    name: '',
    type: '',
    mobileNumber: '',
    email: '',
    logo: null,
    companyProfileId: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FacilityFormData, string>>>({});

  // Derive unique hospitals from facilities, fallback to API if none
  useEffect(() => {
    if (facilities && facilities.length > 0) {
      // Extract unique hospitals by id
      const uniqueHospitals: Hospital[] = [];
      const seen = new Set<string>();
      for (const facility of facilities) {
        if (facility.hospital && facility.hospital.id && !seen.has(facility.hospital.id)) {
          uniqueHospitals.push({ id: facility.hospital.id, name: facility.hospital.name });
          seen.add(facility.hospital.id);
        }
      }
      setHospitals(uniqueHospitals);
      setHospitalLoading(false);
    } else {
      setHospitalLoading(true);
      hospitalApi.getHospitals()
        .then(res => setHospitals(Array.isArray(res) ? res : []))
        .catch(() => setHospitals([]))
        .finally(() => setHospitalLoading(false));
    }
  }, [facilities]);



  // Load facility data in edit mode, after hospitals are done loading (even if empty)
  useEffect(() => {
    if (isEditMode && id && !hospitalLoading) {
      const fetchFacility = async () => {
        setLoading(true);
        try {
          const facility = await facilityApi.getById(id);
          setFormData({
            name: facility.name || '',
            type: facility.type || '',
            mobileNumber: facility.phoneNumber || '',
            email: facility.email || '',
            companyProfileId: facility.hospital?.id || '',
            isActive: facility.isActive ?? true,
          });
        } catch (error) {
          console.error('Error fetching facility:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchFacility();
    }
  }, [id, isEditMode, hospitalLoading, hospitals]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof FacilityFormData;
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when field is edited
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof FacilityFormData;
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when field is edited
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          logo: 'Please upload an image file'
        }));
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          logo: 'File size should be less than 2MB'
        }));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        logo: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous errors
      setErrors(prev => ({
        ...prev,
        logo: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FacilityFormData, string>> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Facility name is required';
    }
    if (!formData.type) {
      newErrors.type = 'Facility type is required';
    }
    
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^[0-9+\-\s]+$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Please enter a valid mobile number';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.companyProfileId) {
      newErrors.companyProfileId = 'Please select a company profile';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      if (isEditMode && id) {
        await facilityApi.update(id, {
          id, // Required by UpdateFacilityDto TypeScript interface
          name: formData.name,
          phoneNumber: formData.mobileNumber,
          email: formData.email,
          isActive: formData.isActive,
          hospitalId: formData.companyProfileId,
          // Add other fields as needed
        });
      } else {
        await facilityApi.create({
          name: formData.name,
          type: formData.type,
          phoneNumber: formData.mobileNumber,
          email: formData.email,
          isActive: formData.isActive,
          hospitalId: formData.companyProfileId,
          // Add other fields as needed
        });
      }
      navigate('/facilities');
      alert(`Facility ${isEditMode ? 'updated' : 'created'} successfully!`);
    } catch (error) {
      console.error('Error saving facility:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Edit Facility' : 'Add New Facility'}
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Avatar
                  src={logoPreview || '/default-facility-logo.png'}
                  sx={{ width: 120, height: 120, fontSize: 48 }}
                >
                  {!logoPreview && (formData.name ? formData.name.charAt(0).toUpperCase() : 'F')}
                </Avatar>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleLogoChange}
                />
                <label htmlFor="logo-upload">
                  <IconButton 
                    color="primary" 
                    aria-label="upload logo" 
                    component="span"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  >
                    <PhotoCamera />
                  </IconButton>
                </label>
              </Box>
              {errors.logo && (
                <FormHelperText error>{errors.logo}</FormHelperText>
              )}
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Click on the icon to upload a logo
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required error={Boolean(errors.type)}>
                <InputLabel>Facility Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleSelectChange}
                  label="Facility Type"
                >
                  <MenuItem value=""><em>Select Facility Type</em></MenuItem>
                  <MenuItem value="HOSPITAL">Hospital</MenuItem>
                  <MenuItem value="CLINIC">Clinic</MenuItem>
                  <MenuItem value="LAB">Lab</MenuItem>
                  <MenuItem value="PHARMACY">Pharmacy</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
                {errors.type && (
                  <FormHelperText>{errors.type}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Facility Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                error={Boolean(errors.name)}
                helperText={errors.name}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                error={Boolean(errors.email)}
                helperText={errors.email}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mobile Number"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                required
                error={Boolean(errors.mobileNumber)}
                helperText={errors.mobileNumber}
                margin="normal"
                InputProps={{
                  startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        isActive: e.target.checked
                      }))}
                      color="primary"
                    />
                  }
                  label={formData.isActive ? 'Active' : 'Inactive'}
                  labelPlacement="start"
                  sx={{ 
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      marginLeft: 1
                    }
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required error={Boolean(errors.companyProfileId)}>
  <InputLabel>Company Profile</InputLabel>
  <Select
    name="companyProfileId"
    value={formData.companyProfileId}
    onChange={handleSelectChange}
    label="Company Profile"
    startAdornment={
      <InputAdornment position="start">
        <Business />
      </InputAdornment>
    }
    disabled={hospitalLoading}
  >
    <MenuItem value="">
      <em>{hospitalLoading ? 'Loading...' : 'Select Company Profile'}</em>
    </MenuItem>
    {(Array.isArray(hospitals) ? hospitals : []).map(hospital => (
      <MenuItem key={hospital.id} value={hospital.id}>
        {hospital.name}
      </MenuItem>
    ))}
  </Select>
  {errors.companyProfileId && (
    <FormHelperText>{errors.companyProfileId}</FormHelperText>
  )}
</FormControl>
            </Grid>

            {/* DEBUG: Show hospitals array */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mt: 2, background: '#f8f8f8', color: 'red' }}>
                <Typography variant="caption">[DEBUG] Hospitals loaded:</Typography>
                <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(hospitals, null, 2)}</pre>
              </Paper>
            </Grid>

            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/facilities')}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
              >
                {submitting ? 'Saving...' : isEditMode ? 'Update Facility' : 'Create Facility'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default FacilityForm;
